import React, {createContext, useContext, useState, ReactNode} from 'react';
import axios from 'axios';

interface User {
    p_id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    country: string;
    // ... any other fields
}

interface UserContextProps {
    user: User | null;
    setUser: (user: User | null) => void;
    refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const useUser = (): UserContextProps => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider = ({children}: UserProviderProps) => {
    const [user, setUser] = useState<User | null>(null);
    const API_URL = import.meta.env.VITE_APP_API_URL;
    const API_USER = `${API_URL}/user`;

    const refreshUser = async () => {
        try {
            const response = await axios.get(API_USER, {headers: {Accept: 'application/json'}});
            const userData = response.data;
            setUser({
                p_id: userData.p_id,
                firstName: userData.first_name,
                lastName: userData.last_name,
                email: userData.email,
                company: userData.company,
                country: userData.country,
                // ... map other fields as needed
            });
        } catch (error) {
            console.error('Failed to refresh user data:', error);
            // Handle error, optionally set user to null or show notification
        }
    };

    return (
        <UserContext.Provider value={{user, setUser, refreshUser}}>
            {children}
        </UserContext.Provider>
    );
};
